import { useState, type ReactNode } from 'react';
import { FILM_TYPES } from '@/lib/catalog';

export interface FilterState {
  type: string;
  year: string;
  sort: string;
}

interface FilterBarProps {
  value: FilterState;
  years: number[];
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

const SORT_OPTIONS = [
  { value: 'updated', label: '按更新时间' },
  { value: 'year', label: '按作品年份' },
  { value: 'title', label: '按名称' },
];

const chipBase =
  'px-3.5 h-9 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer border';

export default function FilterBar({ value, years, onChange, onReset }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const Group = ({ label, children }: { label: string; children: ReactNode }) => (
    <div>
      <p className="text-xs text-foreground-500 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );

  const controls = (
    <div className="flex flex-col gap-5">
      <Group label="类型">
        <button
          onClick={() => onChange({ type: '全部' })}
          className={`${chipBase} ${
            value.type === '全部'
              ? 'bg-accent-500 border-accent-500 text-white'
              : 'bg-background-50 border-background-300 text-foreground-700 hover:border-accent-400'
          }`}
        >
          全部
        </button>
        {FILM_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => onChange({ type: t })}
            className={`${chipBase} ${
              value.type === t
                ? 'bg-accent-500 border-accent-500 text-white'
                : 'bg-background-50 border-background-300 text-foreground-700 hover:border-accent-400'
            }`}
          >
            {t}
          </button>
        ))}
      </Group>

      <Group label="年份">
        <button
          onClick={() => onChange({ year: '全部' })}
          className={`${chipBase} ${
            value.year === '全部'
              ? 'bg-accent-500 border-accent-500 text-white'
              : 'bg-background-50 border-background-300 text-foreground-700 hover:border-accent-400'
          }`}
        >
          全部
        </button>
        {years.map((y) => (
          <button
            key={y}
            onClick={() => onChange({ year: String(y) })}
            className={`${chipBase} ${
              value.year === String(y)
                ? 'bg-accent-500 border-accent-500 text-white'
                : 'bg-background-50 border-background-300 text-foreground-700 hover:border-accent-400'
            }`}
          >
            {y}
          </button>
        ))}
      </Group>

      <Group label="排序">
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange({ sort: opt.value })}
            className={`${chipBase} ${
              value.sort === opt.value
                ? 'bg-primary-500 border-primary-500 text-background-50'
                : 'bg-background-50 border-background-300 text-foreground-700 hover:border-primary-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </Group>

      <button
        onClick={onReset}
        className="self-start text-sm text-foreground-500 hover:text-accent-600 flex items-center gap-1.5 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
      {/* 桌面 */}
      <div className="hidden md:block">{controls}</div>

      {/* 移动端 */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 h-11 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-800 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-accent-600"></i>
            筛选与排序
          </span>
          <i className={mobileOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>
        {mobileOpen && <div className="mt-5">{controls}</div>}
      </div>
    </div>
  );
}